<template>
  <div class="gmf-profile-page gmf-context" v-if="profile">
    <FullBodyAvatar v-if="use3DAvatar" :customer="profile" disablePopup animated :customizable="isMyProfile"
      @click="settingsTab" />

    <div class="gmf-profile-page-container" :class="{ 'gmf-mt-4': !use3DAvatar }">
      <Avatar v-if="!use3DAvatar" :customer="profile" disablePopup :customizable="isMyProfile && isEditableAvatar" />
      <div class="gmf-profile-page-info">
        <div class="gmf-header">
          <CustomerName :customer="profile" :disablePopup="true" />
        </div>
        <CustomerLevel :customer="profile" />
      </div>
    </div>

    <CustomerStatsExpanded :customer="profile" :style="{ marginRight: !use3DAvatar ? 'var(--gmf-p-5)' : '' }" />

    <template v-if="canFollow">
      <button v-if="!isFollowing" class="gmf-btn gmf-btn-primary gmf-profile-page-follow-btn"
        @click.stop.prevent="toggleFollow">
        <Icon icon="follow" /><span>{{ rt('follow') }}</span>
      </button>
      <button v-else class="gmf-btn gmf-btn-secondary gmf-profile-page-follow-btn"
        @click.stop.prevent="toggleFollow">
        <Icon icon="following" /><span>{{ rt('following') }}</span>
      </button>
    </template>

    <template v-if="isMyProfile || profile.publicProfile">
      <template v-if="isMyProfile && notifications?.length">
        <div class="gmf-profile-page-notifications">
          <div class="gmf-subheader gmf-mb-0">{{ t('notifications') }}</div>
          <SocialBannerMini v-if="lootCrateBanner" :banner="lootCrateBanner" style="overflow: hidden;" />
          <EventRow v-for="notification of notifications" :evt="notification" :key="notification.id" />
          <a class="gmf-text-small gmf-link gmf-clickable"
            @click.stop.prevent="openPopupProfileCard(profile.id, 'notifications')" tabindex="0">{{ t('see_more') }}</a>
        </div>
      </template>
      <CustomerAttributes :attributes="profileAttributes" />
    </template>

    <SocialBannerMini v-if="enableSocialBanner" />
  </div>
</template>

<style>
.gmf-profile-page {
  display: flex;
  flex-direction: column;
  gap: var(--gmf-p-4);
  min-height: 100%;
}

/* doesnt contain mini-banner and is mobile */
@media (max-width: 1000px) {
  .gmf-profile-page:not(:has(.gmf-mini-banner)) {
    /* leave space for menu button */
    margin-bottom: 45px;
  }
}

.gmf-profile-page .gmf-mini-banner {
  margin-top: auto;
  width: 100%;
}

.gmf-profile-page .gmf-fullbody-avatar {
  margin-left: calc(0px - var(--gmf-p-4));
  width: calc(100% + var(--gmf-p-4) * 2);
  margin-bottom: 10px;
}

.gmf-profile-page .gmf-fullbody-avatar-content {
  width: 300px;
  transform: unset;
  left: unset;
  inset: unset;
}

.gmf-context.gmf-profile-page .gmf-profile-page-info .gmf-header {
  margin-bottom: 0 !important;
}

.gmf-profile-page-notifications .gmf-subheader {
  width: 100%;
}

.gmf-profile-page .gmf-profile-page-info {
  display: flex;
  flex-direction: column;
  gap: var(--gmf-p-2);
}

.gmf-profile-page-notifications {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gmf-p-3);
}

.gmf-profile-page-notifications .gmf-notif-event-row {
  width: 100%;
}

.gmf-profile-page-container {
  display: flex;
  gap: var(--gmf-p-4);
  align-items: center;
}

.gmf-profile-page-container .gmf-avatar {
  width: 75px;
  height: 75px;
}

.gmf-profile-page .gmf-fullbody-avatar-customizable {
  left: unset;
  right: var(--gmf-p-4);
  transform: translateY(-50%);
}

.gmf-profile-page .gmf-fullbody-avatar-customizable .gmf-icon {
  height: 36px;
  min-width: 36px;
  background: var(--gmf-col-lightgray);
  --gmf-icon-color: var(--gmf-col-nearblack);
}

.gmf-profile-page-follow-btn {
  align-items: center;
  justify-content: center;
  gap: var(--gmf-p-2);

  display: flex !important;
}
</style>

<script>
import state from '@/state';
import callApi from '@/utils/callApi';
import tooltip from '@/utils/tooltip';
import eventbus from '@/utils/eventbus';
import advancedString from '@/utils/advancedString';
const t = state.t.prefix('profile_page');

import openPopupProfileCard from '@/components/PopupProfileCard/openPopupProfileCard';

import Avatar from '@/components/Avatar';
import CustomerName from '@/components/CustomerName';
import CustomerLevel from '@/components/CustomerLevelRow';
import CustomerAttributes from '@/components/CustomerAttributes';
import CustomerStatsExpanded from '@/components/CustomerStatsExpanded';
import FullBodyAvatar from '@/components/FullBodyAvatar';
import EventRow from '@/widgets/Notifications/EventRow';
import SocialBannerMini from '@/components/SocialBannerMini';
import openTokenShop from '@/components/TokenShop/openTokenShop';
import openLootCrate from '@/widgets/LootCrate/openLootCrate';
import Icon from '@/components/Icon';

export default {
  name: 'ProfilePage',
  components: {
    Avatar,
    CustomerName,
    CustomerLevel,
    CustomerAttributes,
    CustomerStatsExpanded,
    FullBodyAvatar,
    EventRow,
    SocialBannerMini,
    Icon,
  },
  props: {
    profile: Object,
  },
  data() {
    return {
      notifications: null,
      lootCrates: [],
      isFollowing: false,
    };
  },
  async mounted() {
    if (this.isMyProfile) {
      let url = '/v1/client/notifications?take=3';
      const res = await callApi('GET', url);
      const body = await res.json();
      this.notifications = body.events;

      this.loadLootCrateStatus();
    }
    if (this.canFollow && this.isLoggedIn) {
      callApi('GET', `/v3/follow/customer/${this.profile.id}`).then(res => res.json()).then(res => {
        this.isFollowing = res.following;
      });
    }
  },
  emits: ['scrollTop'],
  computed: {
    canFollow() {
      return state.merchantConfig?.follow?.customers?.enabled && !this.isMyProfile;
    },
    isLoggedIn () {
      return !! state.profile;
    },
    lootCrateBanner() {
      const lootCratesToOpen = this.lootCrates.filter(lc => {
        if (!lc.quantity || lc.quantity <= 0) {
          return false;
        }
        return true;
      });

      if (lootCratesToOpen.length > 0) {
        const lc = lootCratesToOpen[0];
        const b = {
          imageType: 'lootCrate',
          imageUrl: lc.beforeOpenImageUrl || lc.iconUrl || lc.openImageUrl,
          buttonText: state.t('loot_crates.open_btn'),
          text: advancedString(state.t('loot_crates.new_loot_in_inventory'), {
            lootCrateName: lc.name,
          }),
          quantity: lc.quantity,
          onClick: async () => {
            await openLootCrate(lc);
            this.loadLootCrateStatus();
            this.$emit('scrollTop');
          }
        };
        return b;
      }

      const now = new Date();
      const availableLootCrates = this.lootCrates.filter(lc => {
        if (parseInt(lc.nextOpenTime) !== 0) {
          return false;
        }
        let hasValidPrice = false;
        for (const price of lc.prices) {
          if (price.endsAt && new Date(price.endsAt) <= now) {
            continue;
          }
          if (price.startsAt && new Date(price.startsAt) > now) {
            continue;
          }
          hasValidPrice = true;
          break;
        }
        if (!hasValidPrice) {
          return false;
        }
        return true;
      });
      if (availableLootCrates.length === 0) {
        return null;
      }
      if (!this.showTokenShop) {
        return null;
      }
      const lc = availableLootCrates[0];
      const b = {
        imageType: 'lootCrate',
        imageUrl: lc.beforeOpenImageUrl,
        buttonText: state.t('loot_crates.open_btn'),
        text: advancedString(state.t('loot_crates.new_loot_available'), {
          lootCrateName: lc.name,
        }),
        onClick: openTokenShop,
      };
      return b;
    },
    openLootCrateUrl() {
      if (this.selectedLootCrate) {
        if (this.openAnim && this.selectedLootCrate?.openImageUrl) {
          return this.selectedLootCrate?.openImageUrl;
        }

        return this.selectedLootCrate?.beforeOpenImageUrl || this.selectedLootCrate?.imageUrl;
      }
      return this.selectedLootCrate?.imageUrl;
    },
    enableSocialBanner() {
      return state.merchantConfig?.widgets?.dropdownProfilePage?.socialBanner;
    },
    use3DAvatar() {
      return state.merchantConfig.avatars.enabled === '3D';
    },
    isMyProfile() {
      return state.profile?.id === this.profile.id;
    },
    profileAttributes() {
      return JSON.parse(this.profile.attributesJson);
    },
    showTokenShop() {
      return state.merchantConfig.tokenShop?.enabled;
    },
    isEditableAvatar() {
      return state.merchantConfig.avatars.enabled === 'enabled' || state.merchantConfig.avatars.enabled === '3D';
    },
  },
  methods: {
    t,
    rt: state.t,
    openPopupProfileCard,
    async toggleFollow(ev) {
      if (! this.isLoggedIn) {
        tooltip.create({x: ev.pageX, y: ev.pageY}, this.rt('error_text.log_in_to_follow'));
        return;
      }

      const shouldFollow = !this.isFollowing;
      const res = await callApi(shouldFollow ? 'POST' : 'DELETE', `/v3/follow/customer/${this.profile.id}`);
      if (res.status === 200) {
        this.isFollowing = shouldFollow;
      } else {
        console.error('Failed to follow ' + this.profile.id);
      }
    },
    async loadLootCrateStatus() {
      const lcRes = await callApi('GET', '/v3/customer-loot-crates-status');
      const lcBody = await lcRes.json();
      this.lootCrates = lcBody;
    },
    settingsTab() {
      if (!this.isMyProfile) return;
      eventbus.emit('setCustomerCardTab', 'customer_settings');
    },
  },
};
</script>
